import React, { useState, useRef } from "react";
import { Download, Upload, Loader2, Trash2, Building2, Users, AlertTriangle } from "lucide-react";
import { downloadImportTemplate, importPropertiesFile, importLeadsFile, clearDemoData } from "../../api";
import { useToast } from "../../hooks/use-toast";

const Card = ({ children }) => <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">{children}</div>;

const AdminDataImport = () => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(null);
  const propInput = useRef(null);
  const contactInput = useRef(null);
  const [result, setResult] = useState(null);

  const uploadProps = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy("props");
    try { const r = await importPropertiesFile(f); setResult({ kind: "listings", ...r }); toast({ title: `Imported ${r.created} listing${r.created !== 1 ? "s" : ""}`, description: r.skipped ? `${r.skipped} skipped` : undefined }); }
    catch (err) { toast({ title: "Import failed", description: err?.response?.data?.detail || "Check the file." }); }
    finally { setBusy(null); e.target.value = ""; }
  };

  const uploadContacts = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy("contacts");
    try { const r = await importLeadsFile(f); setResult({ kind: "contacts", ...r }); toast({ title: `Imported ${r.created} contact${r.created !== 1 ? "s" : ""}`, description: r.skipped ? `${r.skipped} skipped` : undefined }); }
    catch (err) { toast({ title: "Import failed", description: err?.response?.data?.detail || "Check the file." }); }
    finally { setBusy(null); e.target.value = ""; }
  };

  const clear = async (listings, contacts) => {
    const what = [listings && "all listings", contacts && "all contacts/leads"].filter(Boolean).join(" and ");
    if (!window.confirm(`This permanently deletes ${what}. This cannot be undone. Continue?`)) return;
    setBusy("clear");
    try { const r = await clearDemoData({ listings, contacts, confirm: true }); toast({ title: "Demo data cleared", description: `${r.listingsRemoved || 0} listings, ${r.contactsRemoved || 0} contacts removed.` }); setResult(null); }
    catch (err) { toast({ title: "Clear failed", description: err?.response?.data?.detail || "Try again." }); }
    finally { setBusy(null); }
  };

  const btn = "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-500 transition-colors disabled:opacity-60";

  return (
    <div className="space-y-6" data-testid="admin-data-import">
      <div>
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b]">Import your real data</h3>
        <p className="text-[13px] text-[#8a8a8a] mt-1">Download a template, fill it in, and upload to replace the demo data with your live properties and contacts.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-2 mb-3"><span className="w-9 h-9 rounded-lg bg-[#b8935f]/10 flex items-center justify-center text-[#b8935f]"><Building2 size={18} /></span><h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Properties</h4></div>
          <p className="text-[13px] text-[#8a8a8a] mb-4">Columns: title, address, postcode, price, type, status, bedrooms, bathrooms, receptions, epc, sqft, summary, description, features (use <code>|</code> between items), image_urls (use <code>|</code>).</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => downloadImportTemplate("properties")} data-testid="download-properties-template" className={`${btn} border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f]`}><Download size={15} /> Template</button>
            <button onClick={() => propInput.current?.click()} disabled={busy === "props"} data-testid="upload-properties-btn" className={`${btn} bg-[#b8935f] text-white hover:bg-[#a67f4d]`}>{busy === "props" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload CSV/XLSX</button>
            <input ref={propInput} type="file" accept=".csv,.xlsx,.xlsm" hidden onChange={uploadProps} data-testid="properties-file-input" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-3"><span className="w-9 h-9 rounded-lg bg-[#b8935f]/10 flex items-center justify-center text-[#b8935f]"><Users size={18} /></span><h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Contacts</h4></div>
          <p className="text-[13px] text-[#8a8a8a] mb-4">Columns: name, email, phone, type (enquiry / valuation / viewing), message. Contacts load straight into your CRM and are auto-scored and assigned.</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => downloadImportTemplate("contacts")} data-testid="download-contacts-template" className={`${btn} border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f]`}><Download size={15} /> Template</button>
            <button onClick={() => contactInput.current?.click()} disabled={busy === "contacts"} data-testid="upload-contacts-btn" className={`${btn} bg-[#b8935f] text-white hover:bg-[#a67f4d]`}>{busy === "contacts" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload CSV/XLSX</button>
            <input ref={contactInput} type="file" accept=".csv,.xlsx,.xlsm" hidden onChange={uploadContacts} data-testid="contacts-file-input" />
          </div>
        </Card>
      </div>

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-[13px] text-green-800" data-testid="import-result">
          Imported <b>{result.created}</b> {result.kind}
          {result.skipped ? (
            <span>, skipped {result.skipped}
              {result.skippedDuplicates ? ` (${result.skippedDuplicates} already in your CRM` : ""}
              {result.skippedMissingEmail ? `${result.skippedDuplicates ? ", " : " ("}${result.skippedMissingEmail} with no valid email` : ""}
              {(result.skippedDuplicates || result.skippedMissingEmail) ? ")" : ""}
            </span>
          ) : ""}.
        </div>
      )}

      <Card>
        <div className="flex items-center gap-2 mb-2"><AlertTriangle size={18} className="text-red-500" /><h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Clear demo data</h4></div>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Remove the sample data before or after importing your own. This is permanent.</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => clear(true, false)} disabled={busy === "clear"} data-testid="clear-listings-btn" className={`${btn} border border-red-200 text-red-600 hover:bg-red-50`}><Trash2 size={15} /> Clear all listings</button>
          <button onClick={() => clear(false, true)} disabled={busy === "clear"} data-testid="clear-contacts-btn" className={`${btn} border border-red-200 text-red-600 hover:bg-red-50`}><Trash2 size={15} /> Clear all contacts</button>
          <button onClick={() => clear(true, true)} disabled={busy === "clear"} data-testid="clear-all-btn" className={`${btn} bg-red-600 text-white hover:bg-red-700`}>{busy === "clear" ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Clear everything</button>
        </div>
      </Card>
    </div>
  );
};

export default AdminDataImport;
